import React, { useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useJourney } from '../../context/JourneyContext';
import { LogOut } from 'lucide-react';
import { TinyStepsLogo } from '../ui/FloatingDecorations';

/* ── Per-item pastel color identity ─────────────────────────────────
   rgb     = base accent color (used for border, glow, dot, active icon)
   light   = very soft tint (used for inactive icon bg, inactive row tint)
   textDark = active label color
   textLight= active subtitle color
   ────────────────────────────────────────────────────────────────── */
const C = {
  home: {
    rgb:              '210,185,205',
    light:            '252,250,255',
    textDark:         '#4a3458',
    textLight:        '#7a6088',
    dotGrad:          'linear-gradient(135deg,#e8b4cc,#c4a0e8)',
    dotGlow:          'rgba(210,155,190,0.50)',
    activeRowBg:      'linear-gradient(140deg,rgba(252,224,244,0.94) 0%,rgba(237,218,252,0.92) 50%,rgba(252,224,244,0.94) 100%)',
    activeBorder:     '1px solid rgba(246,141,179,0.32)',
    activeShadow:     '0 4px 20px rgba(246,141,179,0.14),0 2px 8px rgba(184,138,248,0.10),inset 0 1.5px 0 rgba(255,255,255,0.90)',
    activeIconBg:     'linear-gradient(145deg,rgba(255,251,255,0.94) 0%,rgba(238,210,232,0.76) 55%,rgba(216,188,208,0.58) 100%)',
    activeIconBorder: '1.5px solid rgba(210,185,205,0.36)',
    activeIconShadow: '0 3px 12px rgba(200,158,188,0.20),inset 0 1px 0 rgba(255,255,255,0.82)',
  },
  period:    { rgb:'236,72,153',  light:'255,208,228', textDark:'#b02870', textLight:'#ce7098', dotGrad:'linear-gradient(135deg,#ec4899,#f472b6)',   dotGlow:'rgba(236,72,153,0.62)'  },
  pregnancy: { rgb:'251,146,60',  light:'255,224,194', textDark:'#a85015', textLight:'#c87035', dotGrad:'linear-gradient(135deg,#fb923c,#f97316)',   dotGlow:'rgba(251,146,60,0.60)'  },
  delivery:  { rgb:'251,113,133', light:'255,214,220', textDark:'#a83050', textLight:'#c86070', dotGrad:'linear-gradient(135deg,#fb7185,#f43f5e)',   dotGlow:'rgba(251,113,133,0.62)' },
  medical:   { rgb:'52,211,153',  light:'190,248,228', textDark:'#0a7850', textLight:'#2a9870', dotGrad:'linear-gradient(135deg,#34d399,#10b981)',   dotGlow:'rgba(52,211,153,0.60)'  },
  baby:      { rgb:'251,191,36',  light:'255,238,192', textDark:'#956010', textLight:'#b08030', dotGrad:'linear-gradient(135deg,#fbbf24,#f59e0b)',   dotGlow:'rgba(251,191,36,0.58)'  },
  memories:  { rgb:'192,132,252', light:'238,218,255', textDark:'#7038b8', textLight:'#9060d0', dotGrad:'linear-gradient(135deg,#c084fc,#a78bfa)',   dotGlow:'rgba(192,132,252,0.64)' },
  ai:        { rgb:'167,139,250', light:'226,218,255', textDark:'#5830a8', textLight:'#7858c8', dotGrad:'linear-gradient(135deg,#a78bfa,#8b5cf6)',   dotGlow:'rgba(167,139,250,0.64)' },
  community: { rgb:'252,165,165', light:'255,225,220', textDark:'#a03838', textLight:'#c06060', dotGrad:'linear-gradient(135deg,#fca5a5,#fb7185)',   dotGlow:'rgba(252,165,165,0.62)' },
  profile:   { rgb:'212,170,120', light:'248,238,220', textDark:'#786040', textLight:'#a08055', dotGrad:'linear-gradient(135deg,#d4aa78,#c09060)',   dotGlow:'rgba(212,170,120,0.58)' },
};

const STAGE_META = {
  period_tracking:    { label: 'Track My Cycle',  emoji: '🌸', sub: "Track your cycle, flow & fertility" },
  trying_to_conceive: { label: 'My Body Rhythm',  emoji: '💕', sub: "Fertility tracking & ovulation insights" },
  pregnant:           { label: 'Tiny Miracle',    emoji: '🤰', sub: 'A tiny miracle growing with love' },
  postpartum:         { label: 'Little Wonder',  emoji: '👶', sub: "Treasuring every tiny moment"    },
  baby_care:          { label: 'Little Wonder',  emoji: '👶', sub: 'Treasuring every tiny moment'    },
};

const NAV_PREGNANT = [
  { path:'/pregnancy',       emoji:'🤰', label:'Tiny Miracle',       sub:'Your pregnancy journey',          c: C.home      },
  { path:'/pregnancy-story', emoji:'✨', label:'Pregnancy Story',     sub:'Your miracle, beautifully told',  c: C.pregnancy },
  { path:'/medical',         emoji:'📋', label:'Medical',            sub:'Records & scans',                 c: C.medical   },
  { path:'/delivery',        emoji:'🎀', label:'Delivery Prep',      sub:'Birth plan & hospital bag',       c: C.delivery  },
  { path:'/memories',        emoji:'💌', label:'Memory Vault',       sub:'Precious moments',                c: C.memories  },
  { path:'/ai',              emoji:'✨', label:'AI Companion',       sub:'Your wellness assistant',         c: C.ai        },
  // { path:'/community',       emoji:'💕', label:'Community',          sub:'Connect with other moms',         c: C.community },
  { path:'/profile',         emoji:'⚙️', label:'Profile & Settings', sub: null,                             c: C.profile   },
];

const NAV_BABY = [
  { path:'/dashboard', emoji:'👶', label:'Little Wonder',       sub: null,                             c: C.home      },
  { path:'/baby',      emoji:'🧸', label:'Little Moments',     sub:'Tiny moments, treasured forever.', c: C.baby     },
  { path:'/medical',   emoji:'📋', label:'Medical',            sub:'Vaccines & records',              c: C.medical   },
  { path:'/memories',  emoji:'💌', label:'Memory Vault',       comingSoon: true,                      c: C.memories  },
  { path:'/ai',        emoji:'✨', label:'AI Companion',       sub:'Your wellness assistant',         c: C.ai        },
  // { path:'/community', emoji:'💕', label:'Community',          sub:'Connect with other moms',         c: C.community },
  { path:'/profile',   emoji:'⚙️', label:'Profile & Settings', sub: null,                             c: C.profile   },
];

/* TTC — has Community, no Memory Vault */
const NAV_TTC = [
  { path:'/dashboard', emoji:'💕', label:'My Body Rhythm',      sub: null,                             c: C.home      },
  { path:'/period',    emoji:'🌙', label:'Cycle Story',         sub:'Every cycle tells a story.',      c: C.period    },
  { path:'/medical',   emoji:'📋', label:'Medical',            sub:'Records & documents',             c: C.medical   },
  { path:'/ai',        emoji:'✨', label:'AI Companion',       sub:'Your wellness assistant',         c: C.ai        },
  // { path:'/community', emoji:'💕', label:'Community',          sub:'Connect with others on your journey', c: C.community },
  { path:'/profile',   emoji:'⚙️', label:'Profile & Settings', sub: null,                             c: C.profile   },
];

/* Period Tracking — no Community, no Memory Vault */
const NAV_DEFAULT = [
  { path:'/dashboard', emoji:'🌸', label:'Track My Cycle',      sub: null,                             c: C.home      },
  { path:'/period',    emoji:'🌙', label:'Cycle Story',         sub:'Every cycle tells a story.',      c: C.period    },
  { path:'/medical',   emoji:'📋', label:'Medical',            sub:'Records & documents',             c: C.medical   },
  { path:'/ai',        emoji:'✨', label:'AI Companion',       sub:'Your wellness assistant',         c: C.ai        },
  { path:'/profile',   emoji:'⚙️', label:'Profile & Settings', sub: null,                             c: C.profile   },
];

/* Helper: build all styles for a nav item from its color config + state.
   Optional override keys on `c` (activeRowBg, activeBorder, activeShadow,
   activeIconBg, activeIconBorder, activeIconShadow) take precedence when set. */
const itemStyles = (c, isActive, isHovered) => {
  const rowBg = isActive
    ? (c.activeRowBg || 'linear-gradient(140deg,rgba(252,224,244,0.94) 0%,rgba(237,218,252,0.92) 50%,rgba(252,224,244,0.94) 100%)')
    : isHovered
    ? 'rgba(255,255,255,0.52)'
    : 'rgba(255,255,255,0.26)';

  const border = isActive
    ? (c.activeBorder || '1px solid rgba(246,141,179,0.32)')
    : '1px solid rgba(255,255,255,0.40)';

  const shadow = isActive
    ? (c.activeShadow || '0 4px 20px rgba(246,141,179,0.14),0 2px 8px rgba(184,138,248,0.10),inset 0 1.5px 0 rgba(255,255,255,0.90)')
    : isHovered
    ? '0 2px 8px rgba(0,0,0,0.04),inset 0 1px 0 rgba(255,255,255,0.80)'
    : 'none';

  const iconBg = isActive
    ? (c.activeIconBg || `linear-gradient(135deg,rgba(${c.rgb},0.28),rgba(${c.rgb},0.16))`)
    : `linear-gradient(135deg,rgba(${c.rgb},0.13),rgba(${c.rgb},0.07))`;

  const iconBorder = isActive
    ? (c.activeIconBorder || `1.5px solid rgba(${c.rgb},0.36)`)
    : `1px solid rgba(${c.rgb},0.15)`;

  const iconShadow = isActive
    ? (c.activeIconShadow || `0 2px 10px rgba(${c.rgb},0.16)`)
    : `0 1px 4px rgba(${c.rgb},0.06)`;

  return { rowBg, border, shadow, iconBg, iconBorder, iconShadow };
};

const Sidebar = () => {
  const { user, logout } = useAuth();
  const navigate          = useNavigate();
  const { isPregnant, isBabyMode, isTTC, stage } = useJourney();

  const [hoveredPath, setHoveredPath] = useState(null);

  const stageMeta = STAGE_META[stage] || STAGE_META.period_tracking;
  const navItems  = isPregnant ? NAV_PREGNANT : isBabyMode ? NAV_BABY : isTTC ? NAV_TTC : NAV_DEFAULT;

  const h     = new Date().getHours();
  const greet = h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
  const name  = user?.nickname?.trim() || user?.name?.split(' ')[0] || 'Mama';

  return (
    <aside className="hidden lg:flex flex-col fixed top-0 left-0 bottom-0 w-64 z-40 overflow-hidden"
      style={{
        background: 'linear-gradient(180deg,rgba(255,253,255,0.98) 0%,rgba(252,250,255,0.97) 50%,rgba(255,253,255,0.98) 100%)',
        backdropFilter: 'blur(28px) saturate(160%)',
        WebkitBackdropFilter: 'blur(28px) saturate(160%)',
        borderRight: '1px solid rgba(0,0,0,0.05)',
      }}>

      {/* ── Atmospheric glow blobs ── */}
      <div className="absolute pointer-events-none" aria-hidden style={{
        top:-52, right:-36, width:210, height:210, borderRadius:'50%',
        background:'radial-gradient(circle,rgba(249,114,182,0.06) 0%,transparent 68%)',
      }} />
      <div className="absolute pointer-events-none" aria-hidden style={{
        bottom:44, left:-48, width:190, height:190, borderRadius:'50%',
        background:'radial-gradient(circle,rgba(192,132,252,0.06) 0%,transparent 68%)',
      }} />
      <div className="absolute pointer-events-none" aria-hidden style={{
        top:'44%', right:-20, width:120, height:120, borderRadius:'50%',
        background:'radial-gradient(circle,rgba(252,165,165,0.05) 0%,transparent 68%)',
      }} />
      <div className="absolute pointer-events-none" aria-hidden style={{
        top:'22%', left:-30, width:100, height:100, borderRadius:'50%',
        background:'radial-gradient(circle,rgba(167,139,250,0.05) 0%,transparent 68%)',
      }} />

      {/* Floating ambient sparkles */}
      <span className="absolute pointer-events-none select-none" aria-hidden
        style={{ top:'16%', left:14, fontSize:11, opacity:0.30, animation:'float 8s ease-in-out infinite' }}>✨</span>
      <span className="absolute pointer-events-none select-none" aria-hidden
        style={{ top:'54%', right:12, fontSize:9, opacity:0.22, animation:'float 12s ease-in-out infinite reverse' }}>💗</span>
      <span className="absolute pointer-events-none select-none" aria-hidden
        style={{ bottom:'30%', left:18, fontSize:10, opacity:0.18, animation:'float 9s ease-in-out infinite', animationDelay:'2s' }}>🌸</span>
      <span className="absolute pointer-events-none select-none" aria-hidden
        style={{ top:'36%', right:18, fontSize:8, opacity:0.16, animation:'float 14s ease-in-out infinite reverse', animationDelay:'3s' }}>💜</span>

      {/* ── Logo ── */}
      <div className="flex items-center gap-3 px-5 h-14 shrink-0 relative z-10"
        style={{ borderBottom:'1px solid rgba(0,0,0,0.05)' }}>
        <TinyStepsLogo size={32} />
        <div className="leading-none">
          <p className="text-sm font-display font-bold text-gradient">TinySteps</p>
          <p className="text-[7px] text-gray-400 uppercase tracking-widest font-round">motherhood companion</p>
        </div>
      </div>

      {/* ── Stage badge ── */}
      <div className="px-4 pt-3 pb-2 shrink-0 relative z-10">
        <div style={{
          background:'linear-gradient(140deg,rgba(252,224,244,0.94) 0%,rgba(237,218,252,0.92) 50%,rgba(252,224,244,0.94) 100%)',
          borderRadius:18,
          border:'2px solid rgba(246,141,179,0.42)',
          boxShadow:'0 4px 16px rgba(246,141,179,0.14),inset 0 1px 0 rgba(255,255,255,0.72)',
          padding:'11px 14px',
        }}>
          <div className="flex items-center gap-2.5">
            <span style={{ fontSize:22, lineHeight:1 }}>{stageMeta.emoji}</span>
            <div className="flex-1 min-w-0">
              <p style={{ fontSize:8, color:'#a090b4', letterSpacing:'0.09em', textTransform:'uppercase', marginBottom:2 }}
                className="font-round">Your Stage</p>
              <p style={{ fontSize:12, fontWeight:700, color:'#5e4870', lineHeight:1.2 }}
                className="font-round truncate">{stageMeta.label}</p>
              <p style={{ fontSize:9, color:'#a898b8', fontStyle:'italic', marginTop:2 }}
                className="font-round truncate">{stageMeta.sub}</p>
            </div>
            <button
              onClick={() => navigate('/profile', { state: { tab:'journey', _t: Date.now() } })}
              style={{ fontSize:9, color:'#a878a8', fontWeight:600, background:'none', border:'none', cursor:'pointer', padding:0 }}
              className="font-round shrink-0 hover:opacity-70 transition-opacity">
              Change
            </button>
          </div>
        </div>
      </div>

      {/* ── Nav links ── */}
      <nav className="flex-1 overflow-y-auto px-3 py-2 relative z-10" style={{ scrollbarWidth:'none' }}>
        {navItems.map(({ path, emoji, label, sub, c, comingSoon }) => {
          if (comingSoon) {
            const st = itemStyles(c, false, false);
            return (
              <div key={path} style={{
                display:'flex', alignItems:'center', gap:12,
                padding:'10px 12px', marginBottom:4, borderRadius:18,
                background: st.rowBg, border: st.border,
                cursor:'not-allowed', userSelect:'none', opacity:0.82,
              }}>
                {/* Icon bubble */}
                <div style={{
                  width:36, height:36, borderRadius:12, flexShrink:0,
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:17, lineHeight:1,
                  background: st.iconBg, border: st.iconBorder, boxShadow: st.iconShadow,
                }}>
                  {emoji}
                </div>
                {/* Label + Coming Soon badge */}
                <div className="flex-1 min-w-0">
                  <p style={{
                    fontSize:13, lineHeight:1.2, fontWeight:550,
                    color:'#6a5878', marginBottom:4,
                  }} className="font-round truncate">
                    {label}
                  </p>
                  <span style={{
                    display:'inline-block',
                    background:'linear-gradient(135deg,#FBBF24 0%,#F97316 100%)',
                    color:'white',
                    fontSize:7.5, fontWeight:700,
                    padding:'2.5px 8px', borderRadius:20,
                    letterSpacing:'0.07em', textTransform:'uppercase',
                    boxShadow:'0 2px 8px rgba(249,115,22,0.40)',
                  }}>
                    Coming Soon
                  </span>
                </div>
              </div>
            );
          }

          return (
            <NavLink
              key={path}
              to={path}
              onMouseEnter={() => setHoveredPath(path)}
              onMouseLeave={() => setHoveredPath(null)}
              style={({ isActive }) => {
                const st = itemStyles(c, isActive, hoveredPath === path);
                return {
                  display:'flex', alignItems:'center', gap:12,
                  padding: sub ? '10px 12px' : '9px 12px',
                  marginBottom: 4, borderRadius: 18,
                  background: st.rowBg, border: st.border, boxShadow: st.shadow,
                  transition:'all 0.22s ease', textDecoration:'none',
                };
              }}>
              {({ isActive }) => {
                const st = itemStyles(c, isActive, hoveredPath === path);
                return (
                  <>
                    {/* Emoji icon bubble */}
                    <div style={{
                      width:36, height:36, borderRadius:12, flexShrink:0,
                      display:'flex', alignItems:'center', justifyContent:'center',
                      fontSize:17, lineHeight:1,
                      background: st.iconBg, border: st.iconBorder, boxShadow: st.iconShadow,
                      transition:'all 0.22s ease',
                    }}>
                      {emoji}
                    </div>

                    {/* Label + subtitle */}
                    <div className="flex-1 min-w-0">
                      <p style={{
                        fontSize:13, lineHeight:1.2,
                        fontWeight: isActive ? 700 : 550,
                        color: isActive ? '#3a2d48' : '#6a5878',
                        marginBottom: sub ? 2 : 0,
                        transition:'color 0.22s ease',
                      }} className="font-round truncate">
                        {label}
                      </p>
                      {sub && (
                        <p style={{
                          fontSize:9, lineHeight:1.35, fontStyle:'italic',
                          color: isActive ? '#7a6888' : '#b8a8c4',
                          transition:'color 0.22s ease',
                        }} className="font-round truncate">
                          {sub}
                        </p>
                      )}
                    </div>

                    {/* Active glow dot */}
                    {isActive && (
                      <div style={{
                        width:7, height:7, borderRadius:'50%', flexShrink:0,
                        background: c.dotGrad,
                        boxShadow: `0 0 9px ${c.dotGlow}`,
                      }} />
                    )}
                  </>
                );
              }}
            </NavLink>
          );
        })}
      </nav>

      {/* ── Mama greeting + user row ── */}
      <div className="px-4 py-4 shrink-0 relative z-10"
        style={{ borderTop:'1px solid rgba(0,0,0,0.05)' }}>

        {/* Glassmorphic profile card */}
        <div style={{
          background:'linear-gradient(140deg,rgba(252,224,244,0.94) 0%,rgba(237,218,252,0.92) 50%,rgba(252,224,244,0.94) 100%)',
          borderRadius:16,
          border:'2px solid rgba(246,141,179,0.42)',
          boxShadow:'0 4px 14px rgba(246,141,179,0.14),inset 0 1px 0 rgba(255,255,255,0.72)',
          padding:'10px 12px',
        }}>
          {/* Greeting */}
          <p style={{
            fontSize:10, color:'#7a5a8a', fontStyle:'italic',
            textAlign:'center', marginBottom:10, lineHeight:1.4,
          }} className="font-round">
            {greet}, {name} 💖
          </p>

          {/* User row */}
          <div className="flex items-center gap-2.5">
            {/* Avatar with glow ring */}
            <div style={{
              width:36, height:36, borderRadius:12, flexShrink:0,
              background:'linear-gradient(135deg,#EA86AA,#AF83EC)',
              display:'flex', alignItems:'center', justifyContent:'center',
              color:'white', fontSize:13, fontWeight:700,
              boxShadow:'0 0 0 2.5px rgba(255,255,255,0.90),0 0 0 5px rgba(246,141,179,0.28),0 4px 14px rgba(246,141,179,0.34)',
            }}>
              {(user?.nickname || user?.name)?.charAt(0)?.toUpperCase() || 'M'}
            </div>

            <div className="flex-1 min-w-0">
              <p style={{ fontSize:12, fontWeight:700, color:'#5e4870', lineHeight:1.2 }}
                className="font-round truncate">{user?.nickname?.trim() || user?.name || 'Mama'}</p>
              <p style={{ fontSize:9, color:'#b89ab8' }}
                className="font-round truncate">{user?.email || ''}</p>
            </div>

            <button
              onClick={() => { logout(); navigate('/'); }}
              style={{
                width:30, height:30, borderRadius:10, flexShrink:0,
                display:'flex', alignItems:'center', justifyContent:'center',
                background:'rgba(255,255,255,0.75)',
                border:'2px solid rgba(246,141,179,0.42)',
                color:'#c4a0b8',
                transition:'all 0.20s ease',
                cursor:'pointer',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = 'rgba(248,242,252,0.92)';
                e.currentTarget.style.color = '#7a4870';
                e.currentTarget.style.borderColor = 'rgba(140,80,120,0.18)';
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.75)';
                e.currentTarget.style.color = '#c4a0b8';
                e.currentTarget.style.borderColor = 'rgba(246,141,179,0.22)';
              }}
              title="Sign out">
              <LogOut size={14} />
            </button>
          </div>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
